import type { AxiosRequestConfig } from "axios";
import axios from "./axios";
import { LocalStorageKey } from "../constants/localStorageKeys";

/**
 * Fetching image bytes (thumbnails, previews drawn on the map).
 *
 * Thumbnails live on the object store, on its own host. That host authenticates
 * by signed URL and rejects any request that also carries an `Authorization`
 * header — it answers 400 and the image never loads. The app's default axios
 * instance attaches that header to everything, so image requests need a client
 * that does not.
 *
 * `axios.create()` gives exactly that: the interceptors in utils/axios are
 * registered on the default instance only, so a created one never sees them.
 */
const bareAxios = axios.create();

const readConfig = (): Record<string, any> =>
{
  try
  {
    return JSON.parse(localStorage.getItem(LocalStorageKey.STATIC_CONFIG) ?? "{}") ?? {};
  } catch
  {
    return {};
  }
};

const toUrl = (value: string | undefined, base?: string): URL | null =>
{
  try
  {
    return value ? new URL(value, base) : null;
  } catch
  {
    return null;
  }
};

/** Object-store link signed in its query (S3 / MinIO): it carries its own auth and rejects an `Authorization` header. */
const isPresigned = (u: URL): boolean =>
  ["X-Amz-Signature", "X-Amz-Credential", "Signature"].some((k) => u.searchParams.has(k));

/** "vmrs-api.geohub.vn" → "geohub.vn" (an IP stays whole). */
const siteOf = (host: string): string =>
  /^[\d.]+$/.test(host) || host.includes(":") ? host : host.split(".").slice(-2).join(".");

/**
 * Where an image request goes and whether it carries our token. Relative links are resolved against the API.
 * The token goes ONLY to our own system — the API's site (any host under the same domain, e.g. `vmrs.geohub.vn` and
 * `vmrs-api.geohub.vn`), the OAuth2 host, and hosts listed in `REACT_APP_TOKEN_IMAGE_HOSTS` — never to a provider's
 * CDN, and never to a presigned object-store link.
 */
export const resolveImageRequest = (url: string): { url: string; withToken: boolean } =>
{
  const cfg = readConfig();
  const api = toUrl(cfg.REACT_APP_API_BASE_URL) ?? toUrl(window.location.origin)!;
  const target = toUrl(url, api.href);
  if (!target) return { url, withToken: false };
  if (isPresigned(target)) return { url: target.href, withToken: false };

  // VMRS image proxy (`…/vmrs/api/v1/proxy?provider_code=…&link=…`) built by the backend on ANOTHER environment's host
  // (seen: `https://api.geohub.dev/v1/vmrs/api/v1/proxy` while the app runs on `vmrs-api.geohub.vn`): that host does not
  // accept this environment's token ⇒ 401. Rebuild it on the current VMRS API (same base + prefix as `vmrsGet`), query kept.
  if (/\/api\/v1\/proxy\/?$/.test(target.pathname) && siteOf(target.hostname) !== siteOf(api.hostname))
  {
    const base = String(cfg.REACT_APP_API_BASE_URL ?? "").replace(/\/+$/, "");
    const prefix = String(cfg.REACT_APP_API_SERVICE_PREFIX_VMRS || "vmrs").replace(/^\/+|\/+$/g, "");
    if (base) return { url: `${base}/${prefix}/api/v1/proxy${target.search}`, withToken: true };
  }

  const extra: string[] = Array.isArray(cfg.REACT_APP_TOKEN_IMAGE_HOSTS) ? cfg.REACT_APP_TOKEN_IMAGE_HOSTS : [];
  const ours = new Set([api, toUrl(cfg.REACT_APP_KEY_URL_OAUTH2), ...extra.map((h) => toUrl(h))]
    .filter((u): u is URL => !!u)
    .map((u) => siteOf(u.hostname)));
  return { url: target.href, withToken: ours.has(siteOf(target.hostname)) };
};

/**
 * GET an image as a blob, sending the auth header only to our own system (`resolveImageRequest`).
 *
 * Note the two clients fail differently: the default instance resolves non-401
 * failures with the axios error (see the response interceptor), while the bare
 * one rejects. Callers must handle both — check the status *and* catch.
 */
export const fetchImageBlob = (url: string, config?: AxiosRequestConfig) =>
{
  // Our system ⇒ the app's axios (adds `Authorization: <token_type> <token>` for OAUTH2, refreshes on 401);
  // anywhere else ⇒ a bare client with no header.
  const target = resolveImageRequest(url);
  const client = target.withToken ? axios : bareAxios;

  return client.get(target.url, { responseType: "blob", ...config });
};
